import React, { useMemo, useState } from 'react';
import { AlertTriangle, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight, ChevronDown, ChevronRight, Info, Database, Shield } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES, SEVERITY_CONFIG } from '../shared';
import type { GovernanceStats, ModuleHealth, FamilyHealth, UnifiedQuestionRow, SystemStats } from '../hooks/useGovernanceData';
import type { IssueDomain } from '../../../../../governance';
import { getGuardGovernanceIssues } from '../../../../../practice/logic/guardGovernanceBridge';
interface OverviewDashboardProps {
  stats: GovernanceStats;
  systemStats: SystemStats;
  moduleHealth: ModuleHealth[];
  familyHealthByModule: Record<string, FamilyHealth[]>;
  questionTypeDistribution: Map<string, number>;
  visualTypeDistribution: Map<string, number>;
  topBlockers: UnifiedQuestionRow[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  stats,
  systemStats,
  moduleHealth,
  familyHealthByModule,
  questionTypeDistribution,
  visualTypeDistribution,
  topBlockers,
  pipelineIssueCounts,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  // P-9: Guard → Governance feedback data
  const guardSummary = useMemo(() => getGuardGovernanceIssues(), []);
  const toggleModule = (code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);else next.add(code);
      return next;
    });
  };
  // Total findings across all pipeline stages
  const totalFindings = pipelineIssueCounts ? Object.values(pipelineIssueCounts).reduce((sum, c) => sum + c, 0) : 0;
  return <div className="space-y-6" data-id="element-4181">
      {/* Health Banner — question-level summary */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isHealthy ? 'bg-emerald-50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-4182">
        {isHealthy ? <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-4183" /> : stats.fail > 0 ? <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-4184" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-4185" />}
        <div className="flex-1 min-w-0" data-id="element-4186">
          <span className={`text-sm font-semibold ${isHealthy ? 'text-emerald-800' : stats.fail > 0 ? 'text-red-800' : 'text-amber-800'}`} data-id="element-4187">
            {isHealthy ? 'Alle vragen voldoen aan de kwaliteitseisen' : stats.fail > 0 ? `${stats.fail} ${stats.fail === 1 ? 'vraag' : 'vragen'} afgekeurd` : 'Aandachtspunten gevonden'}
          </span>
          {!isHealthy && <span className={`text-xs ml-2 ${stats.fail > 0 ? 'text-red-700' : 'text-amber-700'}`} data-id="element-4188">
              {[stats.review > 0 && `${stats.review} vereisen review`, totalFindings > 0 && `${totalFindings} bevindingen totaal`].filter(Boolean).join(' · ')}
            </span>}
        </div>
      </div>

      {/* Stats — question-level counts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4189">
        <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-4191" />} color="blue" subtitle="Unieke vragen in het systeem" data-id="element-4190" />
        <StatCard label="Geslaagd (PASS)" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-4193" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van alle vragen`} data-id="element-4192" />
        <StatCard label="Afgekeurd (FAIL)" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-4195" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.blockerCount} blocker-bevindingen`} data-id="element-4194" />
        <StatCard label="Review Nodig" value={stats.review.toLocaleString('nl-NL')} icon={<AlertTriangle className="w-5 h-5" data-id="element-4197" />} color={stats.review === 0 ? 'emerald' : 'amber'} subtitle={`${stats.warningCount} verbeterpunten`} data-id="element-4196" />
      </div>

      {/* Explainer legend */}
      <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-blue-50/50 border border-blue-100" data-id="element-4198">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-4199" />
        <div className="text-xs text-blue-800 leading-relaxed" data-id="element-4200">
          <span className="font-semibold" data-id="element-4201">Hoe lees je deze cijfers?</span>{' '}
          <span className="font-semibold text-red-700" data-id="element-4202">Afgekeurd</span> en{' '}
          <span className="font-semibold text-amber-700" data-id="element-4203">Review</span> tellen
          het aantal <em data-id="element-4204">vragen</em> met die status. Elke vraag kan meerdere{' '}
          <em data-id="element-4205">bevindingen</em> hebben (blocker = blokkerend, verbeterpunt =
          advies). De Pipeline Fasen hieronder tonen het totaal aantal
          bevindingen per controle-fase.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4206">
        {/* Left Column: Top Blockers & Pipeline Stages & System Coverage */}
        <div className="lg:col-span-2 space-y-6" data-id="element-4207">
          {/* System Coverage */}
          <Card className="p-5" data-id="element-4208">
            <div className="mb-4" data-id="element-4209">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4210">
                <Database className="w-4 h-4 text-indigo-500" data-id="element-4211" /> Systeemdekking
              </h3>
              <p className="text-[11px] text-gray-500 mt-1" data-id="element-4212">
                Overzicht van beschikbare componenten en hun gebruik in het
                vragenbestand.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-id="element-4213">
              <div className="bg-indigo-50/50 rounded-lg p-3 border border-indigo-100 text-center" data-id="element-4214">
                <p className="text-2xl font-bold text-indigo-700" data-id="element-4215">
                  {systemStats.visualTypeCount}
                </p>
                <p className="text-xs font-medium text-indigo-600 mt-1" data-id="element-4216">
                  Visual Types
                </p>
              </div>
              <div className="bg-purple-50/50 rounded-lg p-3 border border-purple-100 text-center" data-id="element-4217">
                <p className="text-2xl font-bold text-purple-700" data-id="element-4218">
                  {systemStats.questionTypeCount}
                </p>
                <p className="text-xs font-medium text-purple-600 mt-1" data-id="element-4219">
                  Vraagtypes
                </p>
              </div>
              <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100 text-center" data-id="element-4220">
                <p className="text-2xl font-bold text-blue-700" data-id="element-4221">
                  {systemStats.familyCount}
                </p>
                <p className="text-xs font-medium text-blue-600 mt-1" data-id="element-4222">
                  Families
                </p>
              </div>
              <div className="bg-emerald-50/50 rounded-lg p-3 border border-emerald-100 text-center" data-id="element-4223">
                <p className="text-2xl font-bold text-emerald-700" data-id="element-4224">
                  {systemStats.moduleCount}
                </p>
                <p className="text-xs font-medium text-emerald-600 mt-1" data-id="element-4225">
                  Modules
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-4226">
              <div data-id="element-4227">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider" data-id="element-4228">
                  Top 5 Vraagtypes
                </h4>
                <div className="space-y-2" data-id="element-4229">
                  {Array.from(questionTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm" data-id="element-4230">
                        <span className="text-gray-600 font-mono text-xs truncate mr-2" data-id="element-4231">
                          {type}
                        </span>
                        <span className="font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-xs" data-id="element-4232">
                          {count}
                        </span>
                      </div>)}
                </div>
              </div>
              <div data-id="element-4233">
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider" data-id="element-4234">
                  Top 5 Visuals
                </h4>
                <div className="space-y-2" data-id="element-4235">
                  {Array.from(visualTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm" data-id="element-4236">
                        <span className="text-gray-600 font-mono text-xs truncate mr-2" data-id="element-4237">
                          {type}
                        </span>
                        <span className="font-medium text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-xs" data-id="element-4238">
                          {count}
                        </span>
                      </div>)}
                </div>
              </div>
            </div>
          </Card>

          {/* Top Blockers */}
          <Card className="p-5" data-id="element-4239">
            <div className="flex items-center justify-between mb-4" data-id="element-4240">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4241">
                <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-4242" /> Afgekeurde
                Vragen
                {topBlockers.length > 0 && <span className="text-xs font-normal text-gray-500" data-id="element-4243">
                    (top {topBlockers.length})
                  </span>}
              </h3>
              {topBlockers.length > 0 && <button onClick={() => onNavigateToQuestion('blockers')} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-4244">
                  Bekijk alle {stats.fail} <ArrowRight className="w-3 h-3" data-id="element-4245" />
                </button>}
            </div>

            {topBlockers.length === 0 ? <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-100" data-id="element-4246">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-4247" />
                <p className="text-sm font-medium text-gray-700" data-id="element-4248">
                  Geen afgekeurde vragen!
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-4249">
                  Alle vragen komen door de pipeline.
                </p>
              </div> : <div className="space-y-2" data-id="element-4250">
                {topBlockers.map(row => {
              const firstBlocker = row.pipelineIssues.find(i => i.kind === 'blocker');
              const modConfig = MODULE_CONFIG[row.moduleCode] || {
                bgClass: 'bg-gray-500'
              };
              return <div key={row.questionId} className="flex items-start gap-3 p-3 rounded-lg border border-red-100 bg-red-50/50 hover:bg-red-50 transition-colors cursor-pointer" onClick={() => onNavigateToQuestion(row.questionId)} data-id="element-4251">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${modConfig.bgClass}`} data-id="element-4252" />
                      <div className="min-w-0 flex-1" data-id="element-4253">
                        <div className="flex items-center gap-2 mb-1" data-id="element-4254">
                          <span className="font-mono text-xs font-bold text-gray-900" data-id="element-4255">
                            {row.questionId}
                          </span>
                          <Badge variant="danger" size="sm" data-id="element-4256">
                            FAIL
                          </Badge>
                          <span className="text-[10px] text-gray-500" data-id="element-4257">
                            {row.blockerCount} blocker
                            {row.blockerCount !== 1 ? 's' : ''}
                            {row.warningCount > 0 && ` · ${row.warningCount} verbeterpunt${row.warningCount !== 1 ? 'en' : ''}`}
                          </span>
                        </div>
                        {firstBlocker && <p className="text-sm text-red-800 truncate" data-id="element-4258">
                            <span className="font-semibold text-xs uppercase mr-1" data-id="element-4259">
                              [{firstBlocker.domain}]
                            </span>
                            {firstBlocker.message}
                          </p>}
                      </div>
                    </div>;
            })}
              </div>}
          </Card>

          {/* Pipeline Stages — individual findings per stage */}
          <Card className="p-5" data-id="element-4260">
            <div className="mb-4" data-id="element-4261">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4262">
                <Zap className="w-4 h-4 text-amber-500" data-id="element-4263" /> Pipeline Fasen
              </h3>
              <p className="text-[11px] text-gray-500 mt-1" data-id="element-4264">
                Aantal individuele bevindingen per controle-fase (één vraag kan
                meerdere bevindingen opleveren).
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4265">
              {PIPELINE_STAGES.map(stage => {
              const count = pipelineIssueCounts?.[stage.domain] ?? 0;
              const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
              const statusConfig = {
                ok: {
                  dot: 'bg-emerald-500',
                  bg: 'bg-emerald-50'
                },
                warning: {
                  dot: 'bg-amber-500',
                  bg: 'bg-amber-50'
                },
                error: {
                  dot: 'bg-red-500',
                  bg: 'bg-red-50'
                },
                pending: {
                  dot: 'bg-gray-300',
                  bg: 'bg-gray-50'
                }
              }[status];
              return <div key={stage.name} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50/50" data-id="element-4266">
                    <div className="flex items-center gap-3" data-id="element-4267">
                      <div className={`w-2 h-2 rounded-full ${statusConfig.dot}`} data-id="element-4268" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-4269">
                        {stage.label}
                      </span>
                    </div>
                    {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-4270">
                        {count === 0 ? '0 bevindingen' : `${count} bevindingen`}
                      </Badge>}
                  </div>;
            })}
            </div>
          </Card>
        </div>

        {/* Right Column: Module Health */}
        <div className="space-y-6" data-id="element-4271">
          <Card className="p-5" data-id="element-4272">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-4273">
              <FileText className="w-4 h-4 text-blue-500" data-id="element-4274" /> Module Gezondheid
            </h3>
            <div className="space-y-3" data-id="element-4275">
              {moduleHealth.map(mod => {
              const config = MODULE_CONFIG[mod.code];
              const bgClass = config?.bgClass ?? 'bg-gray-400';
              const isExpanded = expandedModules.has(mod.code);
              const families = familyHealthByModule[mod.code] || [];
              return <div key={mod.code} className="space-y-1.5 border border-gray-100 rounded-lg p-2 bg-white" data-id="element-4276">
                    <div className="flex items-center justify-between text-xs cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors" onClick={() => toggleModule(mod.code)} data-id="element-4277">
                      <div className="flex items-center gap-1.5 font-medium text-gray-700" data-id="element-4278">
                        {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" data-id="element-4279" /> : <ChevronRight className="w-3 h-3 text-gray-400" data-id="element-4280" />}
                        <div className={`w-1.5 h-1.5 rounded-full ${bgClass}`} data-id="element-4281" />
                        {mod.code}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-4282">
                        <span className="text-gray-500" data-id="element-4283">{mod.total} vr</span>
                        <span className={`font-bold ${mod.pct === 100 ? 'text-emerald-600' : mod.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4284">
                          {mod.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mx-1" data-id="element-4285">
                      <div className={`h-full rounded-full ${mod.pct === 100 ? 'bg-emerald-500' : mod.pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${mod.pct}%`
                  }} data-id="element-4286" />
                    </div>

                    {isExpanded && families.length > 0 && <div className="mt-2 pt-2 border-t border-gray-100 space-y-2 pl-4 pr-1" data-id="element-4287">
                        {families.map(fam => <div key={fam.familyId} className="space-y-1" data-id="element-4288">
                            <div className="flex items-center justify-between text-[10px]" data-id="element-4289">
                              <span className="font-mono text-gray-600 truncate mr-2" title={fam.familyId} data-id="element-4290">
                                {fam.familyId}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-4291">
                                <span className="text-gray-400" data-id="element-4292">
                                  {fam.pass}/{fam.total}
                                </span>
                                <span className={`font-semibold ${fam.pct === 100 ? 'text-emerald-600' : fam.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4293">
                                  {fam.pct}%
                                </span>
                              </div>
                            </div>
                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-4294">
                              <div className={`h-full rounded-full ${fam.pct === 100 ? 'bg-emerald-400' : fam.pct >= 80 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                        width: `${fam.pct}%`
                      }} data-id="element-4295" />
                            </div>
                          </div>)}
                      </div>}
                  </div>;
            })}
            </div>
          </Card>

          {/* P-9: Guard Runtime Rejections */}
          <Card className="p-5" data-id="element-4296">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2" data-id="element-4297">
              <Shield className="w-4 h-4 text-orange-500" data-id="element-4298" /> Runtime Guard
            </h3>
            <p className="text-[11px] text-gray-500 mb-3" data-id="element-4299">
              Vragen die door de pedagogische guard worden afgewezen tijdens
              oefensessies.
            </p>
            {guardSummary.totalRejections === 0 ? <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-100" data-id="element-4300">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-4301" />
                <p className="text-xs text-gray-600" data-id="element-4302">
                  Geen runtime afwijzingen
                </p>
              </div> : <div className="space-y-3" data-id="element-4303">
                <div className="grid grid-cols-2 gap-2" data-id="element-4304">
                  <div className="bg-red-50 rounded-lg p-2.5 border border-red-100 text-center" data-id="element-4305">
                    <p className="text-lg font-bold text-red-700" data-id="element-4306">
                      {guardSummary.totalAffectedQuestions}
                    </p>
                    <p className="text-[10px] font-medium text-red-600" data-id="element-4307">
                      Vragen
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-2.5 border border-orange-100 text-center" data-id="element-4308">
                    <p className="text-lg font-bold text-orange-700" data-id="element-4309">
                      {guardSummary.totalRejections}
                    </p>
                    <p className="text-[10px] font-medium text-orange-600" data-id="element-4310">
                      Afwijzingen
                    </p>
                  </div>
                </div>
                {guardSummary.reasonBreakdown.length > 0 && <div className="space-y-1.5" data-id="element-4311">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-4312">
                      Top redenen
                    </p>
                    {guardSummary.reasonBreakdown.slice(0, 3).map(r => <div key={r.reason} className="flex items-start gap-2 text-[11px]" data-id="element-4313">
                        <span className="font-mono text-red-600 font-semibold flex-shrink-0" data-id="element-4314">
                          {r.count}×
                        </span>
                        <span className="text-gray-600 leading-tight" data-id="element-4315">
                          {r.description}
                        </span>
                      </div>)}
                  </div>}
                {guardSummary.issues.length > 0 && <div className="space-y-1.5" data-id="element-4316">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-4317">
                      Meest afgewezen
                    </p>
                    {guardSummary.issues.slice(0, 3).map(issue => <div key={issue.questionId} className="flex items-center justify-between text-[10px]" data-id="element-4318">
                        <span className="font-mono text-gray-700 truncate mr-2" title={issue.questionId} data-id="element-4319">
                          {issue.questionId}
                        </span>
                        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-4320">
                          {issue.fatalCount > 0 && <span className="text-red-600 font-semibold" data-id="element-4321">
                              {issue.fatalCount}F
                            </span>}
                          {issue.softCount > 0 && <span className="text-amber-600 font-semibold" data-id="element-4322">
                              {issue.softCount}S
                            </span>}
                        </div>
                      </div>)}
                  </div>}
                {guardSummary.dataRange && <p className="text-[10px] text-gray-400 pt-1 border-t border-gray-100" data-id="element-4323">
                    Data:{' '}
                    {new Date(guardSummary.dataRange.from).toLocaleDateString('nl-NL')}{' '}
                    —{' '}
                    {new Date(guardSummary.dataRange.to).toLocaleDateString('nl-NL')}
                  </p>}
              </div>}
          </Card>
        </div>
      </div>
    </div>;
}